import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

/**
 * Opens an item named by a URL query parameter (e.g. `/materials?edit=<id>` from the
 * "Несоответствия" page). Once `isReady` (the list has loaded), the item with that id is handed to
 * `onFound` and the parameter is removed from the URL, so a reload or the Back button does not open
 * the dialog again. Returns the id that was asked for but not found (deleted, or not readable), so
 * the page can say so.
 */
export function useDeepLink<T extends { id: string }>(
  param: string,
  items: readonly T[],
  isReady: boolean,
  onFound: (item: T) => void,
): { missingId: string | null; dismiss: () => void } {
  const [searchParams, setSearchParams] = useSearchParams()
  const [missingId, setMissingId] = useState<string | null>(null)
  const handled = useRef<string | null>(null)
  const wanted = searchParams.get(param)

  useEffect(() => {
    if (!wanted) {
      handled.current = null
      return
    }
    // Until the URL update lands, the same parameter may be seen once more: handle it only once.
    if (!isReady || handled.current === wanted) return
    handled.current = wanted
    const item = items.find((candidate) => candidate.id === wanted)
    if (item) onFound(item)
    setMissingId(item ? null : wanted)
    const next = new URLSearchParams(searchParams)
    next.delete(param)
    setSearchParams(next, { replace: true })
  }, [wanted, isReady, items, onFound, param, searchParams, setSearchParams])

  return { missingId, dismiss: () => setMissingId(null) }
}
